import { Component, computed, DestroyRef, inject, signal, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NoteDTO } from '@iota/iota-management-portal-v4-client';
import { IotaModalOptions, IotaModalSize, MAT_DIALOG_DATA, MatDialogRef, ModalService } from '@iota/iota-ui-components';
import { Observable } from 'rxjs';
import { ConfirmationDialogData, DialogConfirmationComponent } from '../dialog-confirmation/dialog-confirmation.component';

export enum ReportReviewDialogType {
    SHOW_NOTES = 'SHOW_NOTES',
    APPROVE = 'APPROVE',
    REJECT = 'REJECT',
    SHOW_ERROR = 'SHOW_ERROR'
}

export interface ReportReviewDialogData {
    dialogType: ReportReviewDialogType;
    reportName: string;
    // Full sentences rather than a report name interpolated here, so each report can be translated with its own grammar.
    confirmText?: string;
    actionErrorText?: string;
    notes: Signal<NoteDTO[]>;
    errorText: Signal<string>;
    busy: Signal<boolean>;
    actionFailed?: Signal<boolean>;
    approve: () => Observable<unknown>;
    reject: () => Observable<unknown>;
    addNote: (note: string) => Observable<unknown>;
    deleteNote: (noteId: string) => Observable<unknown>;
}

const REPORT_REVIEW_DIALOG_MODAL_OPTIONS: Record<ReportReviewDialogType, Partial<IotaModalOptions>> = {
    [ReportReviewDialogType.SHOW_NOTES]: { showToggleFullScreenButton: true, size: IotaModalSize.SIZE_3XL },
    [ReportReviewDialogType.SHOW_ERROR]: { showToggleFullScreenButton: true, size: IotaModalSize.SIZE_7XL },
    [ReportReviewDialogType.APPROVE]: { showToggleFullScreenButton: false, size: IotaModalSize.SMALL },
    [ReportReviewDialogType.REJECT]: { showToggleFullScreenButton: false, size: IotaModalSize.SMALL }
};

@Component({
    standalone: false,
    selector: 'app-report-review-dialog',
    templateUrl: './report-review-dialog.component.html'
})
export class ReportReviewDialogComponent {
    private readonly confirmationDialog = inject(ModalService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly dialogRef = inject<MatDialogRef<ReportReviewDialogComponent>>(MatDialogRef);
    private readonly data = inject<ReportReviewDialogData>(MAT_DIALOG_DATA);

    readonly reportName = this.data.reportName;
    readonly confirmText = this.data.confirmText;
    readonly actionErrorText = this.data.actionErrorText;
    readonly notes = this.data.notes;
    readonly errorText = this.data.errorText;
    readonly busy = this.data.busy;

    private readonly failed = signal(false);
    readonly showActionError = computed(() => this.failed() || !!this.data.actionFailed?.());

    readonly isShowNotesDialog = computed(() => this.data.dialogType === ReportReviewDialogType.SHOW_NOTES);
    readonly isApproveDialog = computed(() => this.data.dialogType === ReportReviewDialogType.APPROVE);
    readonly isRejectDialog = computed(() => this.data.dialogType === ReportReviewDialogType.REJECT);
    readonly isShowErrorDialog = computed(() => this.data.dialogType === ReportReviewDialogType.SHOW_ERROR);

    readonly dialogTitle = computed(() => {
        const reportName = this.reportName;
        const titles: Record<ReportReviewDialogType, string> = {
            [ReportReviewDialogType.SHOW_NOTES]: $localize`${reportName}:reportName: Comments`,
            [ReportReviewDialogType.SHOW_ERROR]: $localize`${reportName}:reportName: Error`,
            [ReportReviewDialogType.APPROVE]: $localize`Approve ${reportName}:reportName:`,
            [ReportReviewDialogType.REJECT]: $localize`Reject ${reportName}:reportName:`
        };

        return titles[this.data.dialogType];
    });

    readonly modalOptions = computed(() => REPORT_REVIEW_DIALOG_MODAL_OPTIONS[this.data.dialogType]);

    onCancelClick(): void {
        this.dialogRef.close();
    }

    addNote(note: NoteDTO): void {
        if (!note?.note?.trim()) return;

        this.runAction(this.data.addNote(note.note));
    }

    removeNote(note: NoteDTO): void {
        const dialogData: ConfirmationDialogData = {
            title: $localize`Delete comment`,
            text: $localize`Are you sure you want to delete the comment?`,
            confirmButtonStyle: 'error'
        };

        this.confirmationDialog
            .open(DialogConfirmationComponent, { data: dialogData })
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((confirmed) => {
                if (confirmed) this.runAction(this.data.deleteNote(note.noteId));
            });
    }

    approve(): void {
        this.runAction(this.data.approve(), true);
    }

    reject(): void {
        this.runAction(this.data.reject(), true);
    }

    private runAction(request: Observable<unknown>, closeOnSuccess = false): void {
        this.failed.set(false);

        request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: () => {
                if (closeOnSuccess) this.dialogRef.close();
            },
            error: () => this.failed.set(true)
        });
    }
}
